import type { PsResponse } from '@patsnap/common-ps-http';
import type psHttp from '@patsnap/common-ps-http';
import { RequestChannel, psHttp$ } from '@patsnap/common-ps-http'
import { currentClientUUID } from '@patsnap/uniform-setting'
import type { ErrorHandlerService } from './error-handler-service'
import { BaseHttpService, type CanBeAnonymousRequestConfig } from './base-http-service'
import type { ICoreDataWrapper } from './exception';
import { includes } from 'lodash';

import {
    ExceptionFactory,
    HttpSuccessException, HttpFailException, isAxiosError,
} from './exception';
import { prepareTokenDeferred } from '@eureka/utils/account';

export const RnDHttpException = ExceptionFactory('$$RnDHttpException')

/**
 * 401,403不要弹出错误码
 */
const ignoreStatus = [401, 403]

export class RnDHttp extends BaseHttpService {
    private _channelsMap: Map<string, RequestChannel<any, CanBeAnonymousRequestConfig>> = new Map()

    constructor(
        public httpClient: typeof psHttp,
        public errorHandlerService: ErrorHandlerService
    ) {
        super(errorHandlerService)
        this.configErrorHandler()
    }

    private configErrorHandler() {
        this.errorHandlerService.addHandler(async error => {
            if (HttpSuccessException.is(error)) {
                this._errorDisplayProxy({
                    code: `${error.data.error_code}`,
                    params: error.data.error_params,
                })
                return true
            }
            if (!HttpFailException.is(error)) {
                return false
            } else if (includes(ignoreStatus, error.response?.status)) {
                return true;
            }
            const httpFailExceptionData = error.response?.data
            if (!httpFailExceptionData) {
                return false
            }
            this._errorDisplayProxy({
                code: `${httpFailExceptionData.numeric_error_code}`,
                params: httpFailExceptionData.error_params,
                message: httpFailExceptionData.error_message,
            })
            return true
        })
    }

    static responseSuccessHandler(res: PsResponse<any>) {
        const data = res.data as any as ICoreDataWrapper<any>
        if (data.status === false) {
            throw HttpSuccessException.mark(res as any)
        }
        return res.data
    }

    static responseFailHandler(error: Error) {
        if (!isAxiosError(error)) {
            throw error
        }
        throw HttpFailException.mark(error as any)
    }

    get headers() {
        return {
            'X-Device-ID': currentClientUUID(),
            'X-API-Version': '1.0',
        }
    }

    async request<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config.headers = {
            ...this.headers,
            ...config.headers || {},
        }
        /**
         * 等待token准备好
         */
        if (!config.anonymous) {
            await prepareTokenDeferred.promise;
        }
        return this.httpClient<T>(config)
            .then(RnDHttp.responseSuccessHandler, RnDHttp.responseFailHandler)
    }

    requestByChannelId<T = any>(channelId: string, config: CanBeAnonymousRequestConfig): Promise<T> {
        if (!this._channelsMap.has(channelId)) {
            // @ts-ignore
            const newChannel = new RequestChannel(psHttp$)
            this._channelsMap.set(channelId, newChannel)
        }
        const channel = this._channelsMap.get(channelId) as RequestChannel<T, CanBeAnonymousRequestConfig>
        return channel.requestAsPromise(config)
    }
}
